import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { Popover } from "antd";
import TableLoader from "./TableLoader";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Initialize pdfMake with fonts
if (typeof pdfMake !== 'undefined') {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
}

// Format number with commas and round to the nearest whole value
const formatAmount = (amount) => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount === 0) return "-";

  return Math.round(numericAmount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const Table = ({ loading, tableData, subCategory, showSearchForm, setShowSearchForm }) => {
  const [sortedData, setSortedData] = useState(null);
  const [sortColumn, setSortColumn] = useState("amount"); // Default sort by amount
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPlacement, setTooltipPlacement] = useState("right");
  const rowRefs = useRef({});
  const tableRef = useRef(null);

  // Get query parameters from URL
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      sortBy: searchParams.get('sortBy')
    };
  };

  // Update URL query parameters
  const updateUrlParams = (column) => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    
    // Set sort column parameter
    searchParams.set('sortBy', column);
    
    // Update URL without reloading the page
    window.history.pushState({}, '', url.toString());
  };

  // Initialize sorting from URL query params
  useEffect(() => {
    if (tableData && Array.isArray(tableData)) {
      const { sortBy } = getQueryParams();
      
      // Set initial sort column from URL or default to "amount"
      const initialSortColumn = sortBy === "deals" ? "deals" : "amount";
      setSortColumn(initialSortColumn);
      
      // Sort data with initial parameters (always descending)
      sortData(tableData, initialSortColumn);
    }
  }, [tableData]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      if (tableData) {
        const { sortBy } = getQueryParams();
        const column = sortBy === "deals" ? "deals" : "amount";
        
        setSortColumn(column);
        sortData(tableData, column);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [tableData]);

  // Helper function to sort data based on column (always descending)
  const sortData = (data, column) => {
    if (!data || !Array.isArray(data)) return;
    
    const newData = [...data];
    const sortField = column === "deals" ? "deal_count" : "amount_sum";
    
    // Always sort in descending order
    newData.sort((a, b) => b[sortField] - a[sortField]);
    
    setSortedData(newData);
  };

  // Check and update tooltip placement when needed
  useEffect(() => {
    const checkTooltipPlacement = () => {
      if (hoveredRow !== null && rowRefs.current[hoveredRow] && tableRef.current) {
        const tableRight = tableRef.current.getBoundingClientRect().right;
        const availableWidth = window.innerWidth - tableRight;
        
        // If less than 250px available on right, show tooltip at top
        // You may need to adjust this value based on your tooltip's width
        if (availableWidth < 250) {
          setTooltipPlacement("topRight");
        } else {
          setTooltipPlacement("right");
        }
      }
    };

    checkTooltipPlacement();
    
    // Check placement on resize and scroll
    window.addEventListener("resize", checkTooltipPlacement);
    window.addEventListener("scroll", checkTooltipPlacement);
    
    return () => {
      window.removeEventListener("resize", checkTooltipPlacement);
      window.removeEventListener("scroll", checkTooltipPlacement);
    };
  }, [hoveredRow]);

  // Calculate dense rank - modified to implement proper dense ranking
  const calculateRank = (index, data) => {
    if (!data || data.length === 0) return 1;
    
    const sortField = sortColumn === "deals" ? "deal_count" : "amount_sum";
    
    // Calculate dense ranking by comparing with the previous item
    if (index === 0) return 1;
    
    const currentValue = data[index][sortField];
    const previousValue = data[index - 1][sortField];
    const previousRank = calculateRank(index - 1, data);
    
    return currentValue === previousValue ? previousRank : previousRank + 1;
  };

  const handleSort = (column) => {
    // Update column state
    setSortColumn(column);
    
    // Sort data (always descending)
    sortData(tableData, column);
    
    // Update URL params
    updateUrlParams(column);
  };

  const handleRowMouseEnter = (index) => {
    setHoveredRow(index);
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(null);
  };

  // Custom tooltip content component
  const NotableDealTooltip = ({ content }) => (
    <div className="w-48">
      <h3 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1 border-gray-200">
        📌 Notable Deals
      </h3>
      <p className="text-xs">{content}</p>
    </div>
  );

  const renderTableData = sortedData || [];

  // Generate sort title text
  const getSortTitle = () => {
    const sub = subCategory === "pe" ? "PE" : "M&A";
    return `Transactions Sorted by ${sortColumn === "deals" ? "Deals" : "Amount"} (Descending)`;
  };

  // Render sort icon based on current sort state
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faSort} size="xs" style={{ color: "#a26f35" }} />;
    }
    
    return <FontAwesomeIcon icon={faSortDown} size="xs" style={{ color: "#a26f35" }} />;
  };

  return (
    <div className="relative">
      {loading ? (
        <TableLoader />
      ) : (
        <div className="text-center overflow-x-hidden md:flex flex-col items-center">
          {/* Added sort title */}
          <div className="text-sm text-gray-600 font-medium mb-2">
            {getSortTitle()}
          </div>
          <table 
            ref={tableRef}
            id="table-to-export"
            className="md:mx-4 bg-[aliceblue] text-xs font-sans rounded-lg overflow-hidden md:text-xl md:leading-[3rem] lg:w-full"
          >
            <thead className="border-b border-[#A26F35]">
              <tr className="text-[#A26F35]">
                <th className="py-2 px-4">Rank</th>
                <th className="text-left">Company Name</th>
                <th className="px-8 cursor-pointer" onClick={() => handleSort("deals")}>
                  #of Deals {renderSortIcon("deals")}
                </th>
                <th className="px-8 py-[0.6rem] cursor-pointer" onClick={() => handleSort("amount")}>
                  Amount $M {renderSortIcon("amount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {renderTableData.length > 0 ? (
                renderTableData.map((company, index) => (
                  <Popover 
                    key={index}
                    content={company.notable_deal ? <NotableDealTooltip content={company.notable_deal} /> : null}
                    placement={tooltipPlacement}
                    overlayClassName="notable-deal-tooltip"
                    visible={hoveredRow === index && company.notable_deal ? true : false}
                    overlayStyle={{ 
                      zIndex: 1050,
                      padding: 0,
                      background: 'white',
                      color: 'black',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8'
                    }}
                    align={{
                      offset: [20, 0], // Adjust horizontal offset as needed
                    }}
                  >
                    <tr 
                      className="border-t-2 hover:bg-gray-100"
                      ref={el => rowRefs.current[index] = el}
                      onMouseEnter={() => handleRowMouseEnter(index)}
                      onMouseLeave={handleRowMouseLeave}
                    >
                      <td>{calculateRank(index, renderTableData)}</td>
                      <td className="text-left">{company.advisor_name}</td>
                      <td>{company.deal_count}</td>
                      <td>{formatAmount(company.amount_sum)}</td>
                    </tr>
                  </Popover>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-4 text-center text-gray-500">No Data Found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Table;